import imageCompression from "browser-image-compression";

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  fileType?: string;
  initialQuality?: number;
  exifOrientation?: number;
  alwaysKeepResolution?: boolean;
  /**
   * URL the library's worker uses to importScripts() itself. Defaults
   * upstream to a jsdelivr CDN URL which our CSP script-src blocks; we
   * override to a same-origin copy (see scripts/copy-vendor-libs.mjs).
   */
  libURL?: string;
}

// Same-origin path served from public/lib/ (populated at install/build time
// by scripts/copy-vendor-libs.mjs). Overriding libURL is required so the
// library's worker doesn't try to fetch from cdn.jsdelivr.net — which CSP
// script-src refuses, silently breaking compression for every uploader.
const LOCAL_LIB_URL = "/lib/browser-image-compression.js";

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  reductionPercent: number;
}

// Maximum file size limits (in MB)
export const MAX_FILE_SIZE_MB = 10; // Reject files larger than 10MB before compression
export const MAX_FILE_SIZE_AFTER_COMPRESSION_MB = 2; // Reject if still > 2MB after compression

// Allowed image MIME types
export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

/**
 * Validates if a file is a valid image file
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
    return {
      valid: false,
      error: `Unsupported file type. Please use: ${ALLOWED_IMAGE_TYPES.join(", ")}`,
    };
  }

  // Check file size (before compression)
  const fileSizeMB = file.size / 1024 / 1024;
  if (fileSizeMB > MAX_FILE_SIZE_MB) {
    return {
      valid: false,
      error: `File is too large (${fileSizeMB.toFixed(2)}MB). Maximum size is ${MAX_FILE_SIZE_MB}MB.`,
    };
  }

  return { valid: true };
}

/**
 * Compresses an image file while maintaining quality
 * Optimized for web display with minimal quality loss
 * Automatically removes EXIF data for privacy and smaller file size
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  // Validate file first
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error || "Invalid image file");
  }

  // Determine optimal file type (prefer JPEG for photos, WebP if supported)
  const shouldConvertToJPEG = file.type === "image/png" && file.size > 500 * 1024; // Convert large PNGs to JPEG
  const outputFileType = shouldConvertToJPEG ? "image/jpeg" : file.type;

  // Default options optimized for quality and size
  const defaultOptions: CompressionOptions = {
    maxSizeMB: 1, // Maximum file size in MB (1MB is good for web)
    maxWidthOrHeight: 1920, // Maximum dimension (good for most displays)
    useWebWorker: true, // Use web worker for better performance
    initialQuality: 0.85, // 85% quality - good balance between size and quality
    fileType: outputFileType,
    exifOrientation: 1, // Remove EXIF data (privacy + smaller file size)
    alwaysKeepResolution: false, // Allow resizing for better compression
    libURL: LOCAL_LIB_URL,
  };

  const compressionOptions = { ...defaultOptions, ...options };

  try {
    // Compress the image
    const compressedFile = await imageCompression(file, compressionOptions);
    
    // Validate compressed file size
    const compressedSizeMB = compressedFile.size / 1024 / 1024;
    if (compressedSizeMB > MAX_FILE_SIZE_AFTER_COMPRESSION_MB) {
      // Try more aggressive compression
      const aggressiveOptions = {
        ...compressionOptions,
        maxSizeMB: MAX_FILE_SIZE_AFTER_COMPRESSION_MB,
        initialQuality: 0.75,
      };
      const moreCompressed = await imageCompression(file, aggressiveOptions);
      
      if (moreCompressed.size / 1024 / 1024 > MAX_FILE_SIZE_AFTER_COMPRESSION_MB) {
        throw new Error(
          `Image is too large even after compression. Please use a smaller image (max ${MAX_FILE_SIZE_AFTER_COMPRESSION_MB}MB).`
        );
      }
      
      return {
        file: moreCompressed,
        originalSize: file.size,
        compressedSize: moreCompressed.size,
        reductionPercent: (1 - moreCompressed.size / file.size) * 100,
      };
    }
    
    // Calculate compression stats
    const originalSize = file.size;
    const compressedSize = compressedFile.size;
    const reductionPercent = (1 - compressedSize / originalSize) * 100;
    
    // Log compression results for debugging (only in development)
    if (process.env.NODE_ENV === "development") {
      const originalSizeMB = (originalSize / 1024 / 1024).toFixed(2);
      const compressedSizeMB = (compressedSize / 1024 / 1024).toFixed(2);
      console.log(
        `Image compressed: ${originalSizeMB}MB → ${compressedSizeMB}MB (${reductionPercent.toFixed(1)}% reduction)`
      );
    }
    
    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      reductionPercent,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Image compression failed. Please try a different image.");
  }
}

/**
 * Compresses an image with aggressive settings for very small file sizes
 * Use this when file size is more important than quality (e.g., inspiration photos)
 */
export async function compressImageAggressive(file: File): Promise<CompressionResult> {
  return compressImage(file, {
    maxSizeMB: 0.5, // 500KB max
    maxWidthOrHeight: 1600,
    initialQuality: 0.75, // 75% quality
  });
}

/**
 * Compresses an image with conservative settings for maximum quality
 * Use this when quality is more important than file size (e.g., signature cake images)
 */
export async function compressImageConservative(file: File): Promise<CompressionResult> {
  return compressImage(file, {
    maxSizeMB: 2, // 2MB max
    maxWidthOrHeight: 2560,
    initialQuality: 0.92, // 92% quality
  });
}

/**
 * Compresses an image optimized for signature cakes
 * Higher quality to showcase product details
 */
export async function compressImageForSignatureCake(file: File): Promise<CompressionResult> {
  return compressImage(file, {
    maxSizeMB: 1.5, // 1.5MB max - good quality for product images
    maxWidthOrHeight: 2048, // High resolution for product display
    initialQuality: 0.88, // 88% quality - excellent for product photos
  });
}

/**
 * Compresses an image optimized for inspiration photos
 * Smaller file size is acceptable since these are reference images
 */
export async function compressImageForInspiration(file: File): Promise<CompressionResult> {
  return compressImage(file, {
    maxSizeMB: 0.8, // 800KB max - smaller for reference images
    maxWidthOrHeight: 1920, // Standard resolution
    initialQuality: 0.80, // 80% quality - good enough for reference
  });
}

